<template>
  <div class="home" :class="[locale === 'ar' ? 'rtl' : 'ltr', { 'language-changing': isLanguageChanging }]">
    <HeroSection />

    <!-- Features Section -->
    <section class="features-section py-20 bg-gradient-to-b from-white to-[#f7f6e1]">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6 relative inline-block">
            {{ t('features.title') }}
            <div class="absolute bottom-0 left-0 w-full h-1 bg-[#0f4640]/20 rounded"></div>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-16">
            {{ t('features.subtitle') }}
          </p>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 w-full max-w-7xl mx-auto">
            <!-- Accounting Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-chart-pie text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.accounting.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.accounting.description') }}</p>
              <router-link to="/financial" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.accounting.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- HR Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-users text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.hr.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.hr.description') }}</p>
              <router-link to="/hr" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.hr.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- Manufacturing Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-industry text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.manufacturing.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.manufacturing.description') }}</p>
              <router-link to="/manufacturing" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.manufacturing.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- Projects Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-project-diagram text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.projects.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.projects.description') }}</p>
              <router-link to="/projects" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.projects.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- CRM Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-handshake text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.crm.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.crm.description') }}</p>
              <router-link to="/crm" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.crm.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- Sales Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-shopping-cart text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.sales.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.sales.description') }}</p>
              <router-link to="/sales" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.sales.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Purchase Management Section -->
    <FeatureSection
      backgroundColor="bg-gradient-to-br from-[#f1f5f9] to-white"
      :title="t('purchase.title')"
      :subtitle="t('purchase.subtitle')"
      :features="[
        {
          icon: 'fas fa-file-invoice',
          title: t('purchase.features[0].title'),
          description: t('purchase.features[0].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f7f6e1]'
        },
        {
          icon: 'fas fa-chart-line',
          title: t('purchase.features[1].title'),
          description: t('purchase.features[1].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f7f6e1]'
        },
        {
          icon: 'fas fa-shopping-cart',
          title: t('purchase.features[2].title'),
          description: t('purchase.features[2].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f7f6e1]'
        }
      ]"
      :ctas="[]"
      image="https://images.unsplash.com/photo-1554224155-6726b3ff858f?auto=format&fit=crop&q=80&w=600"
      imageAlt="Purchase Management"
      :testimonial="{
        text: t('purchase.testimonial.text'),
        author: t('purchase.testimonial.author'),
        position: t('purchase.testimonial.position'),
        textClass: 'text-[#0f4640]'
      }"
      titleClass="text-[#0f4640]"
      subtitleClass="text-[#0f4640]/80"
    />

    <!-- Inventory Management Section -->
    <FeatureSection
      backgroundColor="bg-gradient-to-tr from-[#f7f6e1] to-white"
      :title="t('inventory.title')"
      :subtitle="t('inventory.subtitle')"
      :features="[
        {
          icon: 'fas fa-boxes',
          title: t('inventory.features[0].title'),
          description: t('inventory.features[0].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f1f5f9]'
        },
        {
          icon: 'fas fa-barcode',
          title: t('inventory.features[1].title'),
          description: t('inventory.features[1].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f1f5f9]'
        },
        {
          icon: 'fas fa-warehouse',
          title: t('inventory.features[2].title'),
          description: t('inventory.features[2].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f1f5f9]'
        }
      ]"
      :ctas="[]"
      image="https://blog.velosiot.com/hubfs/Retail%20Inventory%20Management.jpg"
      imageAlt="Inventory Management"
      imageRight
      :testimonial="{
        text: t('inventory.testimonial.text'),
        author: t('inventory.testimonial.author'),
        position: t('inventory.testimonial.position'),
        textClass: 'text-[#0f4640]'
      }"
      titleClass="text-[#0f4640]"
      subtitleClass="text-[#0f4640]/80"
    />

    <!-- Accounting Section -->
    <FeatureSection
      backgroundColor="bg-gradient-to-bl from-[#f1f5f9] to-white"
      :title="t('accounting.title')"
      :subtitle="t('accounting.subtitle')"
      :features="[
        {
          icon: 'fas fa-calculator',
          title: t('accounting.features[0].title'),
          description: t('accounting.features[0].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f7f6e1]'
        },
        {
          icon: 'fas fa-file-invoice-dollar',
          title: t('accounting.features[1].title'),
          description: t('accounting.features[1].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f7f6e1]'
        },
        {
          icon: 'fas fa-chart-pie',
          title: t('accounting.features[2].title'),
          description: t('accounting.features[2].description'),
          iconClass: 'text-[#0f4640]',
          bgClass: 'bg-[#f7f6e1]'
        }
      ]"
      :ctas="[]"
      image="https://images.unsplash.com/photo-1554224154-26032ffc0d07?auto=format&fit=crop&q=80&w=600"
      imageAlt="Accounting System"
      :testimonial="{
        text: t('accounting.testimonial.text'),
        author: t('accounting.testimonial.author'),
        position: t('accounting.testimonial.position'),
        textClass: 'text-[#0f4640]'
      }"
      titleClass="text-[#0f4640]"
      subtitleClass="text-[#0f4640]/80"
    />

    <!-- Invoice System Preview Section -->
    <section class="invoice-preview-section">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <span class="inline-block bg-[#faf6df] text-[#0f4640] px-6 py-2 rounded-lg mb-4 font-bold">
            {{ t('invoicing.badge') }}
          </span>
          <h2 class="text-4xl font-bold text-[#faf6df] mb-6">
            {{ t('invoicing.preview.title') }}
          </h2>
          <p class="text-xl text-[#faf6df] opacity-90 max-w-3xl mx-auto mb-16">
            {{ t('invoicing.preview.subtitle') }}
          </p>
        </div>

        <!-- Invoice Cards Container -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <!-- Standard Invoice Card -->
          <div class="invoice-card">
            <div class="flex justify-between items-start mb-6">
              <div class="text-right">
                <h3 class="text-2xl font-bold text-[#0f4640]">{{ t('invoicing.preview.cards.standard.title') }}</h3>
                <p class="text-[#1f9285]">Tax Invoice</p>
              </div>
              <div class="bg-[#0f4640] text-[#faf6df] px-3 py-1 rounded-full text-sm">
                {{ t('invoicing.preview.cards.standard.type') }}
              </div>
            </div>
            <div class="space-y-4 mb-6">
              <div class="h-8 bg-[#0f4640]/10 rounded"></div>
              <div class="h-8 bg-[#0f4640]/10 rounded w-3/4"></div>
              <div class="h-8 bg-[#0f4640]/10 rounded"></div>
            </div>
            <div class="text-left mt-6">
              <div class="text-2xl font-bold text-[#0f4640]">{{ t('invoicing.preview.cards.standard.amount') }}</div>
            </div>
          </div>

          <!-- Simplified Invoice Card -->
          <div class="invoice-card">
            <div class="flex justify-between items-start mb-6">
              <div class="text-right">
                <h3 class="text-2xl font-bold text-[#0f4640]">{{ t('invoicing.preview.cards.simplified.title') }}</h3>
                <p class="text-[#1f9285]">Simplified Invoice</p>
              </div>
              <div class="bg-[#1f9285] text-[#faf6df] px-3 py-1 rounded-full text-sm">
                {{ t('invoicing.preview.cards.simplified.type') }}
              </div>
            </div>
            <div class="space-y-4 mb-6">
              <div class="h-8 bg-[#0f4640]/10 rounded w-3/4"></div>
              <div class="h-8 bg-[#0f4640]/10 rounded"></div>
              <div class="h-8 bg-[#0f4640]/10 rounded w-1/2"></div>
            </div>
            <div class="text-left mt-6">
              <div class="text-2xl font-bold text-[#0f4640]">{{ t('invoicing.preview.cards.simplified.amount') }}</div>
            </div>
          </div>

          <!-- E-Invoice Card -->
          <div class="invoice-card">
            <div class="flex justify-between items-start mb-6">
              <div class="text-right">
                <h3 class="text-2xl font-bold text-[#0f4640]">{{ t('invoicing.preview.cards.electronic.title') }}</h3>
                <p class="text-[#1f9285]">E-Invoice</p>
              </div>
              <div class="bg-[#0f4640] text-[#faf6df] px-3 py-1 rounded-full text-sm">
                {{ t('invoicing.preview.cards.electronic.type') }}
              </div>
            </div>
            <div class="space-y-4 mb-6">
              <div class="h-8 bg-[#0f4640]/10 rounded"></div>
              <div class="h-8 bg-[#0f4640]/10 rounded w-2/3"></div>
              <div class="h-8 bg-[#0f4640]/10 rounded"></div>
            </div>
            <div class="text-left mt-6">
              <div class="text-2xl font-bold text-[#0f4640]">{{ t('invoicing.preview.cards.electronic.amount') }}</div>
            </div>
          </div>
        </div>

        <!-- Features Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-16">
          <div class="feature-card">
            <div class="feature-icon">
              <i class="fas fa-file-invoice"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ t('invoicing.features.design.title') }}</h3>
            <p class="text-gray-600">{{ t('invoicing.features.design.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <i class="fas fa-check-circle"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ t('invoicing.features.compliant.title') }}</h3>
            <p class="text-gray-600">{{ t('invoicing.features.compliant.description') }}</p>
          </div>

          <div class="feature-card">
            <div class="feature-icon">
              <i class="fas fa-bolt"></i>
            </div>
            <h3 class="text-xl font-bold text-[#0f4640] mb-3">{{ t('invoicing.features.instant.title') }}</h3>
            <p class="text-gray-600">{{ t('invoicing.features.instant.description') }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- Section Navigation -->
    <div class="section-nav">
      <div class="section-nav-dot" v-for="(section, index) in sections" 
           :key="index" 
           :class="{ active: activeSection === index }"
           @click="scrollToSection(index)">
      </div>
    </div>

    <!-- Scroll Progress Indicator -->
    <div class="scroll-progress" :style="{ transform: `scaleX(${scrollProgress / 100})` }"></div>

    <!-- Smart Invoicing System Section -->
    <section class="smart-invoicing-section py-20 bg-white relative overflow-hidden">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 reveal-section">
          <span class="inline-block bg-[#faf6df] text-[#0f4640] px-6 py-2 rounded-lg mb-4 font-bold">
            {{ t('invoicing.badge') }}
          </span>
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6 glow-text">{{ t('invoicing.title') }}</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up">
            {{ t('invoicing.subtitle') }}
          </p>
        </div>

        <div class="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div class="space-y-8 feature-cards-container">
            <div class="feature-box flex items-center gap-4 bg-gradient-to-l from-gray-50 to-white p-6 rounded-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
              <div class="text-[#0f4640] bg-white p-4 rounded-xl shadow-inner icon-container">
                <i class="fas fa-file-invoice text-2xl"></i>
              </div>
              <div class="text-right">
                <h3 class="text-xl font-bold text-[#0f4640] mb-2 hover-underline">{{ t('invoicing.features.design.title') }}</h3>
                <p class="text-gray-600">{{ t('invoicing.features.design.description') }}</p>
              </div>
            </div>

            <div class="feature-box flex items-center gap-4 bg-gradient-to-l from-gray-50 to-white p-6 rounded-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
              <div class="text-[#0f4640] bg-white p-4 rounded-xl shadow-inner icon-container">
                <i class="fas fa-bolt text-2xl"></i>
              </div>
              <div class="text-right">
                <h3 class="text-xl font-bold text-[#0f4640] mb-2 hover-underline">{{ t('invoicing.features.instant.title') }}</h3>
                <p class="text-gray-600">{{ t('invoicing.features.instant.description') }}</p>
              </div>
            </div>

            <div class="feature-box flex items-center gap-4 bg-gradient-to-l from-gray-50 to-white p-6 rounded-xl hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-300">
              <div class="text-[#0f4640] bg-white p-4 rounded-xl shadow-inner icon-container">
                <i class="fas fa-check-circle text-2xl"></i>
              </div>
              <div class="text-right">
                <h3 class="text-xl font-bold text-[#0f4640] mb-2 hover-underline">{{ t('invoicing.features.compliant.title') }}</h3>
                <p class="text-gray-600">{{ t('invoicing.features.compliant.description') }}</p>
              </div>
            </div>
          </div>

          <div class="relative invoice-preview">
            <div class="bg-white rounded-2xl shadow-2xl p-8 transform hover:scale-105 transition-all duration-500 invoice-card">
              <div class="flex justify-between items-center mb-6">
                <div class="flex items-center gap-4">
                  <img src="@/assets/logo.png" alt="Logo" class="w-12 h-12 object-contain" />
                  <div class="text-[#0f4640] font-bold text-lg tracking-wide">INV-2024-001#</div>
                </div>
                <div class="status-badge bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-semibold">
                  {{ t('common.status.paid') }}
                  <span class="status-dot"></span>
                </div>
              </div>
              <div class="space-y-4 invoice-content">
                <div class="h-8 bg-gradient-to-l from-gray-100 to-white rounded-lg w-3/4 animate-pulse"></div>
                <div class="h-8 bg-gradient-to-l from-gray-100 to-white rounded-lg animate-pulse"></div>
                <div class="h-8 bg-gradient-to-l from-gray-100 to-white rounded-lg animate-pulse"></div>
              </div>
              <div class="mt-8 flex justify-between items-center">
                <div class="text-3xl font-bold text-[#0f4640] glow-text">{{ t('invoicing.preview.cards.standard.amount') }}</div>
                <i class="fas fa-credit-card text-3xl text-[#0f4640] opacity-50"></i>
              </div>
              <div class="invoice-decoration"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Tax Declaration System Section -->
    <section class="tax-declaration-section py-20 bg-gradient-to-br from-gray-50 to-white relative overflow-hidden">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16 reveal-section">
          <span class="inline-block bg-[#0f4640] text-[#faf6df] px-6 py-2 rounded-lg mb-4 font-bold">
            {{ t('tax.badge') }}
          </span>
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6 glow-text">{{ t('tax.title') }}</h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto fade-in-up">
            {{ t('tax.subtitle') }}
          </p>
        </div>

        <div class="relative tax-preview">
          <div class="bg-white rounded-3xl shadow-2xl p-10 transform hover:scale-105 transition-all duration-500 tax-card">
            <div class="flex justify-between items-center mb-8">
              <div class="text-[#0f4640] text-2xl font-bold glow-text">{{ locale === 'ar' ? 'هيئة الزكاة والضريبة والجمارك' : 'Zakat, Tax and Customs Authority' }}</div>
              <div class="text-[#0f4640] tracking-wide">VAT-2024-Q1#</div>
            </div>
            <div class="grid grid-cols-3 gap-6 mb-8">
              <div class="stat-box bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300">
                <div class="text-gray-600 text-sm mb-2">{{ t('tax.stats.status.label') }}</div>
                <div class="font-bold text-[#0f4640] text-xl">{{ t('tax.stats.status.value') }}</div>
              </div>
              <div class="stat-box bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300">
                <div class="text-gray-600 text-sm mb-2">{{ t('tax.stats.base.label') }}</div>
                <div class="font-bold text-[#0f4640] text-xl">{{ t('tax.stats.base.value') }}</div>
              </div>
              <div class="stat-box bg-gradient-to-br from-gray-50 to-white p-6 rounded-xl text-center hover:shadow-lg transition-all duration-300">
                <div class="text-gray-600 text-sm mb-2">{{ t('tax.stats.amount.label') }}</div>
                <div class="font-bold text-[#0f4640] text-xl">{{ t('tax.stats.amount.value') }}</div>
              </div>
            </div>
            <div class="flex justify-between items-center">
              <div class="status-badge-active bg-gradient-to-r from-green-100 to-green-50 text-green-800 px-6 py-3 rounded-xl text-sm font-semibold">
                {{ t('tax.status') }}
                <span class="status-dot-active"></span>
              </div>
              <div class="text-[#0f4640] font-semibold">{{ t('tax.period') }}</div>
            </div>
            <div class="tax-decoration"></div>
          </div>
        </div>
      </div>
    </section>

    <!-- Services Section -->
    <section class="features-section py-20 bg-gradient-to-b from-white to-[#f7f6e1]">
      <div class="container mx-auto px-4">
        <div class="text-center mb-16">
          <h2 class="text-4xl font-bold text-[#0f4640] mb-6 relative inline-block">
            {{ t('features.title') }}
            <div class="absolute bottom-0 left-0 w-full h-1 bg-[#0f4640]/20 rounded"></div>
          </h2>
          <p class="text-xl text-gray-600 max-w-3xl mx-auto mb-16">
            {{ t('features.subtitle') }}
          </p>
          
          <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10 w-full max-w-7xl mx-auto">
            <!-- Accounting Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-chart-pie text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.accounting.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.accounting.description') }}</p>
              <router-link to="/financial" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.accounting.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- HR Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-users text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.hr.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.hr.description') }}</p>
              <router-link to="/hr" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.hr.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- Manufacturing Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-industry text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.manufacturing.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.manufacturing.description') }}</p>
              <router-link to="/manufacturing" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.manufacturing.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- Projects Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-project-diagram text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.projects.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.projects.description') }}</p>
              <router-link to="/projects" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.projects.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- CRM Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-handshake text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.crm.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.crm.description') }}</p>
              <router-link to="/crm" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.crm.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>

            <!-- Sales Card -->
            <div class="service-card group bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-[#0f4640]/10">
              <div class="service-icon-wrapper bg-[#f7f6e1] p-4 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-6">
                <i class="fas fa-shopping-cart text-2xl text-[#0f4640]"></i>
              </div>
              <h3 class="service-title text-xl font-bold text-[#0f4640] mb-4">{{ t('features.cards.sales.title') }}</h3>
              <p class="service-description text-gray-600 mb-6">{{ t('features.cards.sales.description') }}</p>
              <router-link to="/sales" class="service-link inline-flex items-center text-[#0f4640] font-semibold hover:text-[#1a635b] transition-colors duration-300">
                <span>{{ t('features.cards.sales.link') }}</span>
                <i :class="locale === 'ar' ? 'fas fa-arrow-left mr-2' : 'fas fa-arrow-right ml-2'"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <ChatBot />
  </div>
</template>

<script>
import FeatureSection from '@/components/FeatureSection.vue'
import { useI18n } from 'vue-i18n'
import { ref, onMounted, onBeforeUnmount } from 'vue'
import AOS from 'aos'
import 'aos/dist/aos.css'
import HeroSection from '@/components/HeroSection.vue'
import ChatBot from '@/components/ChatBot.vue'

export default {
  name: 'HomeView',
  components: {
    FeatureSection,
    HeroSection,
    ChatBot
  },
  setup() {
    const { t, locale } = useI18n({
      useScope: 'global'
    })

    const scrollProgress = ref(0)
    const activeSection = ref(0)
    const sections = ref([])
    const isLanguageChanging = ref(false)
    const languages = [
      { code: 'ar', name: 'العربية', icon: '🇸🇦', dir: 'rtl' },
      { code: 'en', name: 'English', icon: '🇬🇧', dir: 'ltr' }
    ]

    const updateScrollProgress = () => {
      const winScroll = document.documentElement.scrollTop
      const height = document.documentElement.scrollHeight - document.documentElement.clientHeight
      scrollProgress.value = (winScroll / height) * 100
    }

    const scrollToSection = (index) => {
      sections.value[index].scrollIntoView({ behavior: 'smooth' })
    }

    const switchLanguage = async (langCode, animate = true) => {
      if (isLanguageChanging.value) return
      
      isLanguageChanging.value = true
      
      if (animate) {
        document.body.classList.add('language-transition-out')
        await new Promise(resolve => setTimeout(resolve, 300))
      }
      
      locale.value = langCode
      document.documentElement.dir = languages.find(lang => lang.code === langCode)?.dir || 'rtl'
      
      localStorage.setItem('preferredLanguage', langCode)
      
      if (animate) {
        document.body.classList.remove('language-transition-in')
        isLanguageChanging.value = false
      } else {
        isLanguageChanging.value = false
      }
    }

    onMounted(() => {
      const savedLanguage = localStorage.getItem('preferredLanguage')
      if (savedLanguage) {
        switchLanguage(savedLanguage, false)
      }
      
      document.documentElement.dir = languages.find(lang => lang.code === locale.value)?.dir || 'rtl'
      
      sections.value = document.querySelectorAll('section')
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            entry.target.classList.add('visible')
            activeSection.value = Array.from(sections.value).indexOf(entry.target)
          }
        })
      }, { 
        threshold: 0.3,
        rootMargin: '-100px'
      })

      sections.value.forEach(section => {
        observer.observe(section)
      })

      window.addEventListener('scroll', updateScrollProgress)

      AOS.init({
        duration: 1000,
        once: true,
        offset: 100,
        disable: window.innerWidth < 768 // Disable on mobile devices
      })
    })

    onBeforeUnmount(() => {
      window.removeEventListener('scroll', updateScrollProgress)
    })

    return {
      t,
      locale,
      scrollProgress,
      activeSection,
      sections,
      languages,
      isLanguageChanging,
      updateScrollProgress,
      scrollToSection,
      switchLanguage
    }
  }
}
</script>

<style scoped>
/* Section Base Styles */
section {
  padding: 6rem 0;
  position: relative;
  overflow: hidden;
}

/* Section Background Variations */
.section-light {
  background: var(--bg-primary);
}

.section-dark {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: var(--text-light);
}

.section-gradient {
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--secondary-color) 100%);
}

/* Section Pattern Overlay */
.section-pattern {
  position: relative;
}

.section-pattern::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230f4640' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.1;
}

/* Section Headers */
.section-header {
  text-align: center;
  margin-bottom: 4rem;
  position: relative;
}

.section-badge {
  display: inline-block;
  padding: 0.5rem 1.5rem;
  border-radius: 9999px;
  font-weight: 600;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  background: var(--primary-color);
  color: var(--secondary-color);
  transform: translateY(0);
  transition: all 0.3s ease;
}

.section-badge:hover {
  transform: translateY(-2px);
}

.section-title {
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  background: linear-gradient(to right, var(--primary-color), var(--primary-light));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.section-subtitle {
  font-size: 1.125rem;
  color: var(--text-secondary);
  max-width: 600px;
  margin: 0 auto;
}

/* Section Content */
.section-content {
  position: relative;
  z-index: 1;
}

/* Section Grid */
.section-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
}

/* Section Card */
.section-card {
  background: var(--bg-primary);
  border-radius: 1rem;
  padding: 2rem;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  border: 1px solid rgba(15, 70, 64, 0.1);
}

.section-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
}

/* Section Divider */
.section-divider {
  height: 4px;
  width: 60px;
  background: var(--primary-color);
  margin: 2rem auto;
  border-radius: 2px;
}

/* Section Animation */
.section-animate {
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.6s ease;
}

.section-animate.visible {
  opacity: 1;
  transform: translateY(0);
}

/* Section Responsive */
@media (max-width: 768px) {
  section {
    padding: 4rem 0;
  }

  .section-title {
    font-size: 2rem;
  }

  .section-grid {
    grid-template-columns: 1fr;
  }
}

/* Section Dark Mode */
@media (prefers-color-scheme: dark) {
  .section-light {
    background: var(--primary-dark);
    color: var(--text-light);
  }

  .section-card {
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
  }
}

/* Hero Section Specific Styles */
.hero-section {
  margin-top: calc(-1 * var(--header-height)); /* Offset the header height */
  padding-top: var(--header-height); /* Add padding equal to header height */
  min-height: 100vh;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: var(--text-light);
  position: relative;
  overflow: hidden;
}

.hero-content {
  position: relative;
  z-index: 2;
}

.hero-title {
  font-size: 4rem;
  font-weight: 800;
  line-height: 1.2;
  margin-bottom: 1.5rem;
  color: var(--secondary-color);
}

.hero-subtitle {
  font-size: 1.5rem;
  opacity: 0.9;
  margin-bottom: 2rem;
  color: var(--secondary-color);
}

/* Features Section Specific Styles */
.features-section {
  padding: var(--spacing-xl) 0;
  background: var(--bg-light);
  position: relative;
  overflow: hidden;
}

.feature-section.bg-dark {
  background: var(--primary-color);
  color: var(--text-light);
}

.section-title {
  font-size: var(--text-3xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-md);
  line-height: 1.3;
  font-family: 'Cairo', sans-serif;
}

.bg-dark .section-title {
  color: var(--text-light);
}

.section-subtitle {
  font-size: var(--text-lg);
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: var(--spacing-xl);
  font-family: 'Noto Sans Arabic', sans-serif;
}

.bg-dark .section-subtitle {
  color: var(--bg-light);
  opacity: 0.9;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  padding: var(--spacing-lg);
  margin-bottom: var(--spacing-md);
  background: var(--bg-white);
  border-radius: var(--radius-lg);
  border: 1px solid rgba(15, 70, 64, 0.1);
  transition: var(--transition-smooth);
  box-shadow: var(--shadow-sm);
}

.bg-dark .feature-item {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.feature-item:hover {
  transform: translateY(-5px);
  box-shadow: var(--shadow-lg);
  border-color: var(--primary-color);
}

.feature-icon {
  width: 48px;
  height: 48px;
  background: var(--bg-light);
  color: var(--primary-color);
  border-radius: var(--radius-md);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: var(--spacing-md);
  font-size: var(--text-xl);
  transition: var(--transition-base);
}

.bg-dark .feature-icon {
  background: var(--bg-light);
  color: var(--primary-color);
}

.feature-item:hover .feature-icon {
  background: var(--primary-color);
  color: var(--text-light);
  transform: rotate(10deg);
}

.feature-content {
  flex: 1;
}

.feature-title {
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--text-primary);
  margin-bottom: var(--spacing-xs);
  font-family: 'Cairo', sans-serif;
}

.bg-dark .feature-title {
  color: var(--text-light);
}

.feature-description {
  font-size: var(--text-base);
  color: var(--text-secondary);
  line-height: 1.6;
  font-family: 'Noto Sans Arabic', sans-serif;
}

.bg-dark .feature-description {
  color: var(--bg-light);
  opacity: 0.9;
}

.testimonial {
  margin-top: var(--spacing-xl);
  padding: var(--spacing-lg);
  background: var(--bg-white);
  border-radius: var(--radius-lg);
  border-right: 4px solid var(--primary-color);
  box-shadow: var(--shadow-md);
}

.bg-dark .testimonial {
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-right-color: var(--bg-light);
}

.testimonial-text {
  font-size: var(--text-lg);
  color: var(--text-primary);
  font-style: italic;
  margin-bottom: var(--spacing-sm);
  line-height: 1.6;
  font-family: 'Noto Sans Arabic', sans-serif;
}

.bg-dark .testimonial-text {
  color: var(--text-light);
}

.testimonial-author {
  font-size: var(--text-base);
  color: var(--primary-color);
  font-weight: 600;
  font-family: 'Cairo', sans-serif;
}

.bg-dark .testimonial-author {
  color: var(--bg-light);
}

/* Feature Image Styles */
.feature-image {
  border-radius: var(--radius-lg);
  overflow: hidden;
  box-shadow: var(--shadow-xl);
  transition: var(--transition-smooth);
}

.feature-image img {
  width: 100%;
  height: auto;
  object-fit: cover;
  transition: var(--transition-smooth);
}

.feature-image:hover {
  transform: translateY(-10px);
}

.feature-image:hover img {
  transform: scale(1.05);
}

/* Call to Action Button */
.cta-button {
  display: inline-flex;
  align-items: center;
  padding: var(--spacing-sm) var(--spacing-lg);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--primary-color);
  background: var(--bg-light);
  border-radius: var(--radius-md);
  transition: var(--transition-base);
  text-decoration: none;
  box-shadow: var(--shadow-md);
}

.bg-dark .cta-button {
  background: var(--bg-light);
  color: var(--primary-color);
}

.cta-button:hover {
  transform: translateY(-2px);
  box-shadow: var(--shadow-lg);
  background: var(--bg-white);
}

/* Responsive Adjustments */
@media (max-width: 768px) {
  .features-section {
    padding: var(--spacing-lg) 0;
  }

  .section-title {
    font-size: var(--text-2xl);
  }

  .section-subtitle {
    font-size: var(--text-base);
  }

  .feature-item {
    padding: var(--spacing-md);
  }

  .feature-icon {
    width: 40px;
    height: 40px;
    font-size: var(--text-lg);
  }

  .feature-title {
    font-size: var(--text-lg);
  }

  .feature-description {
    font-size: var(--text-sm);
  }

  .testimonial {
    margin-top: var(--spacing-lg);
    padding: var(--spacing-md);
  }
}

/* RTL Support */
[dir="rtl"] .feature-item:hover {
  transform: translateX(5px) translateY(-5px);
}

[dir="rtl"] .feature-icon {
  margin-right: 0;
  margin-left: var(--spacing-md);
}

/* Invoice Preview Section Specific Styles */
.invoice-preview-section {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: var(--text-light);
}

.invoice-card {
  background: var(--bg-primary);
  border-radius: 1rem;
  padding: 2rem;
  transition: all 0.3s ease;
}

.invoice-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
}

/* Smart Invoicing Section Specific Styles */
.smart-invoicing-section {
  background: var(--bg-primary);
}

.feature-box {
  background: var(--bg-primary);
  border-radius: 1rem;
  padding: 2rem;
  transition: all 0.3s ease;
  border: 1px solid rgba(15, 70, 64, 0.1);
}

.feature-box:hover {
  transform: translateX(10px);
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
}

/* Tax Declaration Section Specific Styles */
.tax-declaration-section {
  background: linear-gradient(135deg, var(--bg-primary), var(--secondary-color));
}

.tax-card {
  background: var(--bg-primary);
  border-radius: 1rem;
  padding: 2rem;
  transition: all 0.3s ease;
}

.tax-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 25px rgba(0, 0, 0, 0.1);
}

/* Animation Keyframes */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes slideUp {
  from {
    transform: translateY(20px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

/* Animation Classes */
.fade-in {
  animation: fadeIn 1s ease-out forwards;
}

.slide-up {
  animation: slideUp 1s ease-out forwards;
}

.float {
  animation: float 6s ease-in-out infinite;
}

/* Update Color Variables */
.home {
  --primary-color: #0f4640;
  --primary-light: #1f9285;
  --secondary-color: #faf6df;
  --accent-color: #faf6df;
  --text-light: #faf6df;
  --transition-base: all 0.3s ease;
  --transition-smooth: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

/* Enhanced Hero Section Styles */
.hero-section {
  position: relative;
  overflow: hidden;
  background: linear-gradient(135deg, rgba(15, 70, 64, 0.95), rgba(31, 146, 133, 0.95));
}

.hero-section video {
  object-fit: cover;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 0;
  left: 0;
  z-index: 0;
  filter: brightness(0.8) contrast(1.2);
}

.hero-title {
  background: linear-gradient(to right, #faf6df, rgba(250, 246, 223, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 40px rgba(250, 246, 223, 0.3);
  letter-spacing: -0.02em;
}

.hero-subtitle {
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.2);
}

.dashboard-img {
  filter: drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15));
  transform-style: preserve-3d;
  perspective: 1000px;
}

.floating-card,
.floating-card-2 {
  backdrop-filter: blur(12px);
  border: 1px solid rgba(250, 246, 223, 0.2);
  box-shadow: 
    0 10px 30px rgba(0, 0, 0, 0.1),
    0 1px 2px rgba(0, 0, 0, 0.05);
}

.floating-card:hover,
.floating-card-2:hover {
  transform: translateY(-5px) scale(1.02);
  box-shadow: 
    0 20px 40px rgba(0, 0, 0, 0.15),
    0 1px 3px rgba(0, 0, 0, 0.1);
}

/* Enhanced Animations */
@keyframes float {
  0%, 100% {
    transform: translateY(0) rotate(0deg);
  }
  50% {
    transform: translateY(-20px) rotate(1deg);
  }
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delay-1 {
  animation: float 6s ease-in-out 1s infinite;
}

.animate-float-delay-2 {
  animation: float 6s ease-in-out 2s infinite;
}

/* Responsive Styles */
@media (max-width: 760px) {
  .hero-title {
    font-size: 1rem;
  }
  
  .hero-subtitle {
    font-size: 1.25rem;
  }
  
  .floating-card,
  .floating-card-2 {
    padding: 1rem;
  }
}

/* RTL Specific Styles */
[dir="rtl"] .hero-title {
  background: linear-gradient(to left, #faf6df, rgba(250, 246, 223, 0.8));
  -webkit-background-clip: text;
  background-clip: text;
}

[dir="rtl"] .fa-arrow-right {
  transform: rotate(180deg);
}

/* Enhanced Invoice Preview Section */
.invoice-preview-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23faf6df' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
  opacity: 0.1;
  animation: patternFloat 30s linear infinite;
}

@keyframes patternFloat {
  0% {
    background-position: 0 0;
  }
  100% {
    background-position: 100px 100px;
  }
}

.invoice-card {
  background: linear-gradient(145deg, #ffffff, var(--secondary-color));
  border-radius: 24px;
  padding: 2.5rem;
  transform: translateY(0) rotateX(0);
  transition: var(--transition-smooth);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(31, 146, 133, 0.1);
  box-shadow: 0 15px 35px rgba(15, 70, 64, 0.1);
  transform-style: preserve-3d;
  perspective: 1000px;
}

.invoice-card:hover {
  transform: translateY(-15px) rotateX(5deg);
  box-shadow: 0 30px 60px rgba(15, 70, 64, 0.15);
  border-color: var(--primary-light);
}

.invoice-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 8px;
  background: linear-gradient(90deg, var(--primary-color), var(--primary-light));
  border-radius: 24px 24px 0 0;
  transform: scaleX(0.95);
  transition: var(--transition-smooth);
}

.invoice-card:hover::before {
  transform: scaleX(1);
}

.invoice-card::after {
  content: '';
  position: absolute;
  top: 8px;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(31, 146, 133, 0.1), transparent);
  opacity: 0;
  transition: var(--transition-smooth);
}

.invoice-card:hover::after {
  opacity: 1;
}

.invoice-header {
  color: var(--primary-color);
  font-size: 2rem;
  font-weight: bold;
  text-align: center;
  margin-bottom: 2rem;
  position: relative;
}

.invoice-header::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: var(--primary-light);
  border-radius: 2px;
}

.status-badge {
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  color: var(--text-light);
  padding: 0.5rem 1rem;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.875rem;
  transition: var(--transition-smooth);
  position: relative;
  overflow: hidden;
}

.status-badge::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(
    transparent,
    rgba(255, 255, 255, 0.2),
    transparent
  );
  transform: rotate(45deg);
  transition: var(--transition-smooth);
}

.invoice-card:hover .status-badge::before {
  animation: shine 1.5s infinite;
}

@keyframes shine {
  0% {
    transform: rotate(45deg) translateX(-150%);
  }
  50%, 100% {
    transform: rotate(45deg) translateX(150%);
  }
}

/* Enhanced Feature Cards */
.feature-card {
  background: linear-gradient(145deg, var(--secondary-color), #ffffff);
  border-radius: 20px;
  padding: 2.5rem;
  transition: var(--transition-smooth);
  position: relative;
  overflow: hidden;
  border: 1px solid rgba(15, 70, 64, 0.1);
  box-shadow: 0 10px 30px rgba(15, 70, 64, 0.05);
  transform-style: preserve-3d;
  perspective: 1000px;
}

.feature-card:hover {
  transform: translateY(-15px) rotateX(5deg);
  box-shadow: 0 30px 60px rgba(15, 70, 64, 0.15);
  border-color: var(--primary-light);
}

.feature-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  opacity: 0;
  transition: var(--transition-smooth);
  z-index: -1;
}

.feature-card:hover::before {
  opacity: 0.05;
}

.feature-icon {
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  color: var(--text-light);
  font-size: 1.75rem;
  transition: var(--transition-smooth);
  position: relative;
  overflow: hidden;
}

.feature-card:hover .feature-icon {
  transform: rotateY(180deg) scale(1.1);
  box-shadow: 0 10px 20px rgba(15, 70, 64, 0.2);
}

.feature-card:hover .feature-icon::after {
  animation: iconShine 1.5s infinite;
}

/* Enhanced Section Navigation */
.section-nav-dot {
  width: 12px;
  height: 12px;
  background: var(--primary-color);
  border: 2px solid var(--secondary-color);
  border-radius: 50%;
  margin: 8px 0;
  opacity: 0.6;
  transition: var(--transition-smooth);
  cursor: pointer;
  position: relative;
}

.section-nav-dot::before {
  content: '';
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translateY(-50%);
  background: var(--primary-color);
  color: var(--secondary-color);
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  opacity: 0;
  transition: var(--transition-smooth);
  white-space: nowrap;
}

.section-nav-dot:hover::before {
  opacity: 1;
  right: 25px;
}

.section-nav-dot.active {
  opacity: 1;
  transform: scale(1.3);
  background: var(--primary-light);
}

/* Enhanced Mobile Responsiveness */
@media (max-width: 768px) {
  .hero-section {
    min-height: auto;
    padding: 4rem 0;
  }

  .invoice-card,
  .feature-card {
    transform: none !important;
    padding: 2rem;
  }

  .feature-icon {
    width: 50px;
    height: 50px;
    font-size: 1.25rem;
  }

  .invoice-preview-section {
    padding: 4rem 0;
  }
}

/* RTL Specific Animations */
[dir="rtl"] .feature-card:hover .group-hover\:translate-x-2 {
  transform: translateX(-0.5rem);
}

[dir="rtl"] .ml-2 {
  margin-left: 0;
  margin-right: 0.5rem;
}

/* Responsive Adjustments */
@media (min-width: 769px) and (max-width: 1024px) {
  .service-card {
    min-height: 280px;
    padding: 2rem;
  }

  .container {
    max-width: 90%;
    margin: 0 auto;
  }
}

@media (min-width: 1025px) {
  .container {
    max-width: 1200px;
    margin: 0 auto;
  }
}

/* Update styles to remove underlines */
.router-link,
.router-link-active,
.router-link:hover,
.router-link:focus,
a {
  text-decoration: none !important;
}

.feature-card:hover .hover-underline::after,
.hover-underline::after,
a::after {
  display: none !important;
  width: 0 !important;
}

.feature-box:hover .hover-underline::after {
  width: 0 !important;
  display: none !important;
}

/* Remove hover underline animations */
.hover-underline,
.hover-underline::after,
.feature-box .hover-underline::after {
  text-decoration: none !important;
  border-bottom: none !important;
}

/* Add these new animations to your existing styles */
.animate-fade-in {
  animation: fadeIn 1s ease-out forwards;
}

.animate-fade-in-delay {
  animation: fadeIn 1s ease-out 0.3s forwards;
  opacity: 0;
}

.animate-fade-in-up-0 {
  animation: fadeInUp 0.6s ease-out 0.4s forwards;
  opacity: 0;
}

.animate-fade-in-up-1 {
  animation: fadeInUp 0.6s ease-out 0.6s forwards;
  opacity: 0;
}

.animate-fade-in-up-2 {
  animation: fadeInUp 0.6s ease-out 0.8s forwards;
  opacity: 0;
}

.animate-float {
  animation: float 6s ease-in-out infinite;
}

.animate-float-delay-1 {
  animation: float 6s ease-in-out 1s infinite;
}

.animate-float-delay-2 {
  animation: float 6s ease-in-out 2s infinite;
}

/* Update hero section styles */
.hero-section {
  position: relative;
  overflow: hidden;
}

.hero-section video {
  object-fit: cover;
  width: 100%;
  height: 100%;
  position: absolute;
  top: 10;
  left: 0;
  z-index: 0;
}

.benefit-item {
  backdrop-filter: blur(8px);
  border: 1px solid rgba(250, 246, 223, 0.1);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
}

.floating-card,
.floating-card-2 {
  backdrop-filter: blur(8px);
  border: 1px solid rgba(250, 246, 223, 0.2);
}

.dashboard-img {
  filter: drop-shadow(0 25px 25px rgba(0, 0, 0, 0.15));
}

/* Services Section Styles */
.services-section {
  background: linear-gradient(135deg, var(--bg-primary) 0%, var(--secondary-color) 10%);
  position: relative;
  overflow: visible;
  width: 100%;
  padding: 2rem 0;
}

.service-card {
  background: var(--bg-primary);
  border-radius: 1.5rem;
  padding: 2rem;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(15, 70, 64, 0.1);
  position: relative;
  overflow: visible;
  height: auto;
  min-height: 300px;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 100%;
}

.service-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--primary-color), var(--primary-light));
  opacity: 0;
  transition: opacity 0.4s ease;
  z-index: 0;
}

.service-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(15, 70, 64, 0.1);
  border-color: var(--primary-color);
}

.service-icon-wrapper {
  width: 60px;
  height: 60px;
  background: var(--secondary-color);
  border-radius: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
  transition: all 0.4s ease;
  border: 1px solid rgba(15, 70, 64, 0.1);
}

.service-icon-wrapper i {
  font-size: 1.5rem;
  color: #0f4640;
  transition: all 0.4s ease;
}

.service-card:hover .service-icon-wrapper {
  background: #0f4640;
  transform: rotate(10deg) scale(1.1);
  border-color: #0f4640;
}

.service-card:hover .service-icon-wrapper i {
  color: var(--secondary-color);
  transform: rotate(-10deg);
}

.service-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 1rem;
  position: relative;
  z-index: 1;
}

.service-description {
  color: var(--text-secondary);
  line-height: 1.6;
  margin-bottom: 1.5rem;
  position: relative;
  z-index: 1;
  font-size: 0.95rem;
}

.service-link {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--primary-color);
  font-weight: 600;
  transition: all 0.3s ease;
  position: relative;
  z-index: 1;
}

.service-link i {
  transition: transform 0.3s ease;
}

.service-card:hover .service-link {
  color: var(--primary-light);
}

.service-card:hover .service-link i {
  transform: translateX(-5px);
}

@media (max-width: 768px) {
  .service-card {
    min-height: 250px;
    padding: 1.5rem;
    margin-bottom: 1rem;
  }
  
  .service-icon-wrapper {
    width: 3rem;
    height: 3rem;
  }
  
  .service-icon-wrapper i {
    font-size: 1.25rem;
  }
  
  .service-title {
    font-size: 1.25rem;
  }
  
  .service-description {
    font-size: 0.875rem;
  }
}

/* RTL Specific Styles */
[dir="rtl"] .service-card:hover .service-link i {
  transform: translateX(5px);
}
</style> 